// Camera scanning — chapter 7 §7.2 (R7.5 multi-code capture). Per frame, a worker runs
// our own localiser (locate.ts: barcode-shaped regions at any angle, honest rectangles)
// and ZXing C++ via WebAssembly (zxing-wasm, no metering) on each region's crop rotated
// to horizontal. This module owns the camera, frame scheduling, tracking decoded codes
// across frames so outlines stay put, the per-code state, and delivering each code once
// to the scan pipeline with its symbology identifier intact (]C1 GS1-128, ]C0 plain
// Code 128, ]d2, ]Q3…).

import { cropBounds, locateFactor, type Point } from "./locate.js";
import type { BitmapRequest, CropRequest, DecodeRequest, DecodedCode, FrameResult, LocateRequest, RegionsResult, WorkerResponse } from "./scan.worker.js";

/** seen: outlined, not yet judged · accepted: valid GS1 · partial: GS1 data lacking what the
 *  identity needs, waiting for a complementary code on the same label · plain: a non-GS1
 *  code (carton number etc., usable as a lookup identifier) · rejected: malformed GS1 data. */
export type CodeState = "seen" | "accepted" | "partial" | "plain" | "rejected";

/** AIM symbology identifiers whose data is GS1 element strings (or a GTIN). */
export function isGs1Symbology(scanData: string): boolean {
  return /^\](C1|d2|Q3|e0|e1|e2|E0|E4|E1)/.test(scanData) || /^\](Q1|d1)https?:\/\//i.test(scanData);
}

export type Quad = { topLeft: Point; topRight: Point; bottomRight: Point; bottomLeft: Point };

export interface TrackedCode {
  key: string;
  scanData: string; // symbology identifier + decoded text (FNC1 as ASCII 29)
  format: string;
  /** Outline in video pixels. */
  position: Quad;
  state: CodeState;
  firstSeen: number;
  lastSeen: number;
  /** Frames this exact string has been read in. */
  reads: number;
  /** When the frame that last showed this code was captured (performance.now()). */
  observedAt: number;
  /** Motion of the outline in video pixels per ms, from the last two observations. */
  velocity: Point;
}

export interface CameraScannerOptions {
  video: HTMLVideoElement;
  overlay: HTMLCanvasElement;
  /** Called once per distinct code; return the state to show. */
  onCode: (scanData: string, format: string) => Promise<CodeState>;
  /** Minimum interval between frames in ms (a frame is sent only when the worker is idle). */
  intervalMs?: number;
  /** Forget a code's outline after it has not been seen for this long. */
  expireMs?: number;
  /** Requested capture resolution (long side); the device gives the nearest it supports. */
  resolution?: CaptureResolution;
  /** Camera zoom factor to apply when the track supports it (1 = none). */
  zoom?: number;
  /** Frames a code must be read in before it is delivered (guards against a misread
   *  that happens to pass the symbology checksum; seen in the field as rejected GS1
   *  strings with two wrong digits). Default 2. */
  confirmFrames?: number;
}

export type CaptureResolution = 1280 | 1920 | 3840;

function lerp(a: Point, b: Point, k: number): Point {
  return { x: a.x + (b.x - a.x) * k, y: a.y + (b.y - a.y) * k };
}

function quadOf(c: [Point, Point, Point, Point]): Quad {
  return { topLeft: c[0], topRight: c[1], bottomRight: c[2], bottomLeft: c[3] };
}

function centerOf(q: Quad): Point {
  return { x: (q.topLeft.x + q.bottomRight.x) / 2, y: (q.topLeft.y + q.bottomRight.y) / 2 };
}

/** Blend an outline towards a new reading, matching corners so a flipped reading
 *  (the same rectangle reported from the other end) does not twist the outline. */
function smooth(prev: Quad, next: Quad, k: number): Quad {
  const p = [prev.topLeft, prev.topRight, prev.bottomRight, prev.bottomLeft];
  const n = [next.topLeft, next.topRight, next.bottomRight, next.bottomLeft];
  let best = 0;
  let bestD = Infinity;
  for (let s = 0; s < 4; s++) {
    let d = 0;
    for (let i = 0; i < 4; i++) d += Math.hypot(p[i]!.x - n[(i + s) % 4]!.x, p[i]!.y - n[(i + s) % 4]!.y);
    if (d < bestD) {
      bestD = d;
      best = s;
    }
  }
  const out = p.map((q, i) => lerp(q, n[(i + best) % 4]!, k)) as [Point, Point, Point, Point];
  return quadOf(out);
}

export class CameraScanner {
  private stream: MediaStream | null = null;
  private running = false;
  private busy = false;
  private timer: number | null = null;
  private worker: Worker | null = null;
  private sentAt = 0;
  private failures = 0;
  private resolution: CaptureResolution = 3840;
  /** Last worker failure, for the stats line and diagnostics uploads. */
  lastError: string | null = null;
  private readonly tracked = new Map<string, TrackedCode>();
  /** The current frame at capture resolution; crops for decoding are cut from it. */
  private readonly canvas = document.createElement("canvas");
  private readonly ctx = this.canvas.getContext("2d", { willReadFrequently: true })!;
  /** Reduced copy of the frame for localisation (never more than LOCATE_BUDGET_PX). */
  private readonly small = document.createElement("canvas");
  private readonly smallCtx = this.small.getContext("2d", { willReadFrequently: true })!;
  /** Regions decoded per frame, strongest first. */
  readonly maxDecodes = 6;
  /** Hand frames over as ImageBitmaps and let the worker do all pixel work (keeps the
   *  main thread free for the viewfinder). Falls back to the two-step canvas path if the
   *  worker cannot do it. */
  private bitmapPath = typeof OffscreenCanvas !== "undefined" && typeof createImageBitmap === "function";
  /** Main-thread time per frame on the canvas path, or the worker's capture time on the bitmap path. */
  private lastCaptureMs: number | null = null;
  readonly intervalMs: number;
  readonly expireMs: number;

  readonly confirmFrames: number;
  private zoom: number;

  constructor(private readonly opts: CameraScannerOptions) {
    this.intervalMs = opts.intervalMs ?? 30;
    this.expireMs = opts.expireMs ?? 900;
    this.confirmFrames = opts.confirmFrames ?? 2;
    this.zoom = opts.zoom ?? 1;
  }

  async start(): Promise<void> {
    this.resolution = this.opts.resolution ?? 3840;
    this.startWorker();
    await this.openStream();
    this.running = true;
    this.schedule();
  }

  private startWorker(): void {
    this.worker?.terminate();
    this.worker = new Worker(new URL("./scan.worker.ts", import.meta.url), { type: "module" });
    this.worker.onmessage = (ev: MessageEvent<WorkerResponse>) => {
      const m = ev.data;
      if (m.type === "error") this.onFailure(m.message);
      else if (m.type === "regions") this.onRegions(m);
      else this.onResult(m);
    };
    this.worker.onerror = (e) => this.onFailure(`worker: ${e.message}`);
    this.busy = false;
  }

  /** The actual capture resolution in use (may drop after repeated worker failures). */
  get captureResolution(): CaptureResolution {
    return this.resolution;
  }

  private async openStream(): Promise<void> {
    for (const t of this.stream?.getTracks() ?? []) t.stop();
    const res = this.resolution;
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: false,
      video: {
        facingMode: { ideal: "environment" },
        // Reading distance is set by pixels per module: at 720p a Czech GS1-128 read only
        // within ~15 cm (field test 2026-10-27). Localisation runs on a reduced copy;
        // decoding uses crops cut from the full frame (see sendFrame/onRegions).
        width: { ideal: res },
        height: { ideal: Math.round((res * 9) / 16) },
        // Continuous focus matters more than resolution for dense 1D codes.
        advanced: [{ focusMode: "continuous" } as MediaTrackConstraintSet],
      },
    });
    const v = this.opts.video;
    v.srcObject = this.stream;
    v.setAttribute("playsinline", "true"); // iOS: stay inline, not fullscreen
    v.muted = true;
    await v.play();
    if (this.zoom !== 1) await this.setZoom(this.zoom);
  }

  /** Largest zoom factor the track offers, or null when zoom is not controllable here
   *  (older iOS Safari; desktop webcams). */
  get maxZoom(): number | null {
    const track = this.stream?.getVideoTracks()[0];
    const caps = track?.getCapabilities?.() as (MediaTrackCapabilities & { zoom?: { min: number; max: number } }) | undefined;
    return caps?.zoom && caps.zoom.max > 1 ? caps.zoom.max : null;
  }

  /** Zoom in on the camera itself: more pixels per module at the same distance, which
   *  keeps dense codes readable from where the lens can still focus. */
  async setZoom(zoom: number): Promise<void> {
    this.zoom = zoom;
    const track = this.stream?.getVideoTracks()[0];
    const max = this.maxZoom;
    if (!track || max === null) return;
    try {
      await track.applyConstraints({ advanced: [{ zoom: Math.min(zoom, max) } as MediaTrackConstraintSet] });
    } catch (e) {
      this.lastError = `zoom: ${e instanceof Error ? e.message : String(e)}`;
    }
  }

  stop(): void {
    this.running = false;
    if (this.timer !== null) window.clearTimeout(this.timer);
    this.timer = null;
    this.worker?.terminate();
    this.worker = null;
    for (const t of this.stream?.getTracks() ?? []) t.stop();
    this.stream = null;
    this.opts.video.srcObject = null;
    this.tracked.clear();
    this.draw();
  }

  get torchSupported(): boolean {
    const track = this.stream?.getVideoTracks()[0];
    const caps = track?.getCapabilities?.() as (MediaTrackCapabilities & { torch?: boolean }) | undefined;
    return Boolean(caps?.torch);
  }

  async setTorch(on: boolean): Promise<void> {
    const track = this.stream?.getVideoTracks()[0];
    if (!track) return;
    try {
      await track.applyConstraints({ advanced: [{ torch: on } as MediaTrackConstraintSet] });
    } catch {
      /* unsupported on this device */
    }
  }

  /** Every code delivered this session, in order, for diagnostics uploads: a sweep is
   *  judged by this log, not by what happens to be in view at capture time. */
  readonly history: { at: number; scanData: string; format: string; state: CodeState; center: Point; reads: number }[] = [];
  private readonly startedAt = performance.now();

  codes(): TrackedCode[] {
    return [...this.tracked.values()];
  }

  /** Timing of recent frames (ms per frame: locate + decodes, in the worker) for diagnostics. */
  readonly decodeTimes: number[] = [];
  readonly locateTimes: number[] = [];
  private regions = 0;
  private frameCount = 0;
  private fpsWindowStart = performance.now();
  fps = 0;

  stats(): { lastDecodeMs: number | null; avgDecodeMs: number | null; lastLocateMs: number | null; captureMs: number | null; path: "bitmap" | "canvas"; fps: number; resolution: string; zoom: number | null; regions: number; lastError: string | null } {
    const n = this.decodeTimes.length;
    const avg = n ? this.decodeTimes.reduce((a, b) => a + b, 0) / n : null;
    const v = this.opts.video;
    const settings = this.stream?.getVideoTracks()[0]?.getSettings() as (MediaTrackSettings & { zoom?: number }) | undefined;
    return {
      zoom: settings?.zoom ?? null,
      captureMs: this.lastCaptureMs,
      path: this.bitmapPath ? "bitmap" : "canvas",
      lastDecodeMs: n ? this.decodeTimes[n - 1]! : null,
      avgDecodeMs: avg,
      lastLocateMs: this.locateTimes.length ? this.locateTimes[this.locateTimes.length - 1]! : null,
      fps: this.fps,
      resolution: `${v.videoWidth}x${v.videoHeight}`,
      regions: this.regions,
      lastError: this.lastError,
    };
  }

  /** Current frame as a JPEG data URL (full capture resolution) for diagnostics uploads. */
  snapshot(quality = 0.85): string | null {
    const v = this.opts.video;
    if (!v.videoWidth) return null;
    this.canvas.width = v.videoWidth;
    this.canvas.height = v.videoHeight;
    this.ctx.drawImage(v, 0, 0, v.videoWidth, v.videoHeight);
    return this.canvas.toDataURL("image/jpeg", quality);
  }

  /** Update a code's state later (e.g. a partial code completed by a neighbour). */
  markState(scanData: string, state: CodeState): void {
    const t = this.tracked.get(scanData);
    if (t) t.state = state;
  }

  private schedule(): void {
    if (!this.running) return;
    this.timer = window.setTimeout(() => this.tick(), this.intervalMs);
  }

  private tick(): void {
    if (!this.running) return;
    // A step that never comes back (worker hung or died silently) must not stop scanning.
    if (this.busy && performance.now() - this.sentAt > 4000) this.onFailure("frame timed out");
    if (!this.busy) {
      try {
        this.sendFrame();
      } catch (err) {
        this.onFailure(`capture: ${err instanceof Error ? err.message : String(err)}`);
      }
    }
    this.expire();
    this.draw();
    this.schedule();
  }

  /** Step 1 of a frame: draw the video at capture resolution, send a reduced copy for
   *  localisation. The full frame stays in `canvas` until the regions come back. */
  private sendFrame(): void {
    const v = this.opts.video;
    if (!this.worker || v.readyState < 2 || v.videoWidth === 0) return;
    const w = v.videoWidth;
    const h = v.videoHeight;
    if (this.bitmapPath) {
      this.busy = true;
      this.sentAt = performance.now();
      this.countFrame();
      const worker = this.worker;
      createImageBitmap(v)
        .then((bitmap) => {
          if (this.worker !== worker || !this.running) {
            bitmap.close();
            return;
          }
          const msg: BitmapRequest = { type: "bitmap", bitmap, maxDecodes: this.maxDecodes };
          worker.postMessage(msg, [bitmap]);
        })
        .catch((e: unknown) => this.onFailure(`bitmap: ${e instanceof Error ? e.message : String(e)}`));
      return;
    }
    const t0 = performance.now();
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w;
      this.canvas.height = h;
    }
    this.ctx.drawImage(v, 0, 0, w, h);
    const factor = locateFactor(w, h);
    const sw = Math.round(w / factor);
    const sh = Math.round(h / factor);
    if (this.small.width !== sw || this.small.height !== sh) {
      this.small.width = sw;
      this.small.height = sh;
    }
    const smallData = factor === 1 ? this.ctx.getImageData(0, 0, w, h) : (this.smallCtx.drawImage(this.canvas, 0, 0, w, h, 0, 0, sw, sh), this.smallCtx.getImageData(0, 0, sw, sh));
    const buffer = smallData.data.buffer as ArrayBuffer;
    const msg: LocateRequest = { type: "locate", data: buffer, width: sw, height: sh, scale: w / sw };
    this.busy = true;
    this.sentAt = performance.now();
    this.countFrame();
    this.worker.postMessage(msg, [buffer]);
    this.lastCaptureMs = Math.round(performance.now() - t0);
  }

  private countFrame(): void {
    this.frameCount++;
    const now = performance.now();
    if (now - this.fpsWindowStart >= 1000) {
      this.fps = Math.round((this.frameCount * 1000) / (now - this.fpsWindowStart));
      this.frameCount = 0;
      this.fpsWindowStart = now;
    }
  }

  /** Step 2: cut each region's bounding box out of the full-resolution frame and send
   *  those for decoding. Only these sub-images are ever read back from a large canvas. */
  private onRegions(res: RegionsResult): void {
    if (!this.worker || !this.running) {
      this.busy = false;
      return;
    }
    this.regions = res.regions.length;
    this.locateTimes.push(res.locateMs);
    if (this.locateTimes.length > 60) this.locateTimes.shift();
    const w = this.canvas.width;
    const h = this.canvas.height;
    const crops: CropRequest[] = [];
    for (const region of res.regions.slice(0, this.maxDecodes)) {
      const b = cropBounds(region, w, h);
      if (!b) continue;
      const img = this.ctx.getImageData(b.x0, b.y0, b.width, b.height);
      crops.push({ data: img.data.buffer as ArrayBuffer, width: b.width, height: b.height, x0: b.x0, y0: b.y0, region });
    }
    if (crops.length === 0) {
      this.onResult({ type: "result", codes: [], regions: this.regions, locateMs: res.locateMs, totalMs: res.locateMs });
      return;
    }
    const msg: DecodeRequest = { type: "decode", crops, regions: this.regions, locateMs: res.locateMs };
    this.sentAt = performance.now();
    this.worker.postMessage(msg, crops.map((c) => c.data));
  }

  private onFailure(message: string): void {
    this.lastError = message;
    console.warn("scan frame failed", message);
    if (this.bitmapPath) {
      // The bitmap hand-off is an optimisation; any trouble with it means the canvas path.
      this.bitmapPath = false;
      this.lastError = `${message} → canvas path`;
      this.startWorker();
      return;
    }
    this.failures++;
    this.startWorker();
    // Three failures in a row at this resolution: step down and carry on.
    if (this.failures >= 3 && this.resolution > 1280) {
      this.failures = 0;
      this.resolution = this.resolution === 3840 ? 1920 : 1280;
      this.lastError = `${message} → ${this.resolution === 1920 ? "1080p" : "720p"}`;
      void this.openStream().catch((e: unknown) => console.warn("reopen failed", e));
    }
  }

  private onResult(res: FrameResult): void {
    this.busy = false;
    this.failures = 0;
    if (!this.running) return;
    this.decodeTimes.push(res.totalMs);
    if (this.decodeTimes.length > 60) this.decodeTimes.shift();
    if (res.captureMs !== undefined) {
      this.lastCaptureMs = res.captureMs;
      this.locateTimes.push(res.locateMs);
      if (this.locateTimes.length > 60) this.locateTimes.shift();
    }
    this.regions = res.regions;
    const now = performance.now();
    // Positions belong to the moment the frame was captured, not to now: the pipeline is
    // 100–250 ms long on a phone, and the outline is extrapolated over that age when drawn.
    const capturedAt = this.sentAt;
    for (const c of res.codes) this.observe(c, now, capturedAt);
  }

  private observe(c: DecodedCode, now: number, capturedAt: number): void {
    const key = c.scanData;
    const position = quadOf(c.corners);
    const existing = this.tracked.get(key);
    if (existing) {
      const dt = capturedAt - existing.observedAt;
      if (dt > 0) {
        const prev = centerOf(existing.position);
        const next = centerOf(position);
        const v = { x: (next.x - prev.x) / dt, y: (next.y - prev.y) / dt };
        existing.velocity = lerp(existing.velocity, v, 0.6);
      }
      existing.position = smooth(existing.position, position, 0.8);
      existing.observedAt = capturedAt;
      existing.lastSeen = now;
      existing.reads++;
      if (existing.reads === this.confirmFrames && !this.verdicts.has(key)) this.deliver(existing);
      return;
    }
    // A code judged earlier in this session (owner 2026-10-29: the camera reads the same
    // carton again as soon as it comes back into view) keeps its verdict and colour; it is
    // never delivered twice, so the flow sees no duplicate.
    const earlier = this.verdicts.get(key);
    const t: TrackedCode = { key, scanData: c.scanData, format: c.format, position, state: earlier ?? "seen", firstSeen: now, lastSeen: now, reads: 1, observedAt: capturedAt, velocity: { x: 0, y: 0 } };
    this.tracked.set(key, t);
    if (earlier) return;
    if (this.confirmFrames <= 1) this.deliver(t);
  }

  private deliver(t: TrackedCode): void {
    const p = t.position;
    const entry = { at: Math.round(performance.now() - this.startedAt), scanData: t.scanData, format: t.format, state: t.state, center: { x: (p.topLeft.x + p.bottomRight.x) / 2, y: (p.topLeft.y + p.bottomRight.y) / 2 }, reads: t.reads };
    this.history.push(entry);
    this.verdicts.set(t.key, "seen");
    void this.opts.onCode(t.scanData, t.format).then((state) => {
      entry.state = state;
      this.verdicts.set(t.key, state);
      const cur = this.tracked.get(t.key);
      if (cur) cur.state = state;
    });
  }

  /** Verdicts for the session: a code is judged once, then only recoloured. */
  private readonly verdicts = new Map<string, CodeState>();

  private expire(): void {
    const now = performance.now();
    for (const [k, t] of this.tracked) if (now - t.lastSeen > this.expireMs) this.tracked.delete(k);
  }

  /** Draw outlines on the overlay canvas, mapped from video pixels to the displayed size. */
  private draw(): void {
    const c = this.opts.overlay;
    const v = this.opts.video;
    const rect = v.getBoundingClientRect();
    if (c.width !== Math.round(rect.width) || c.height !== Math.round(rect.height)) {
      c.width = Math.round(rect.width);
      c.height = Math.round(rect.height);
    }
    const g = c.getContext("2d");
    if (!g) return;
    g.clearRect(0, 0, c.width, c.height);
    if (!v.videoWidth) return;
    // object-fit: cover → uniform scale, centred crop.
    const scale = Math.max(c.width / v.videoWidth, c.height / v.videoHeight);
    const dx = (c.width - v.videoWidth * scale) / 2;
    const dy = (c.height - v.videoHeight * scale) / 2;
    const now = performance.now();
    for (const t of this.tracked.values()) {
      const p = t.position;
      // Carry the outline forward by its motion over the age of its last observation, so
      // it sits where the code is now rather than where it was when the frame was taken.
      const age = Math.min(300, now - t.observedAt);
      const ox = t.velocity.x * age;
      const oy = t.velocity.y * age;
      const pts = [p.topLeft, p.topRight, p.bottomRight, p.bottomLeft].map((q) => ({ x: (q.x + ox) * scale + dx, y: (q.y + oy) * scale + dy }));
      const colour = { accepted: "#22c55e", partial: "#f59e0b", plain: "#38bdf8", rejected: "#ef4444", seen: "#94a3b8" }[t.state];
      const fill = { accepted: "rgba(34,197,94,0.18)", partial: "rgba(245,158,11,0.18)", plain: "rgba(56,189,248,0.18)", rejected: "rgba(239,68,68,0.18)", seen: "rgba(148,163,184,0.12)" }[t.state];
      g.lineWidth = 4;
      g.strokeStyle = colour;
      g.fillStyle = fill;
      g.setLineDash(t.state === "partial" ? [10, 8] : []);
      g.beginPath();
      g.moveTo(pts[0]!.x, pts[0]!.y);
      for (let i = 1; i < 4; i++) g.lineTo(pts[i]!.x, pts[i]!.y);
      g.closePath();
      g.fill();
      g.stroke();
    }
  }
}
